/**
 * Registro Central de Efeitos
 * Configuração com lazy loading para otimização de bundle
 *
 * NOTA: Os nomes e descrições agora vêm do sistema de tradução (i18n)
 * Acesse via: t(`effects.names.${effectId}`) e t(`effects.descriptions.${effectId}`)
 */

export const EFFECTS_REGISTRY = {
  halftone: {
    id: 'halftone',
    // name e description agora vêm de i18n: effects.names.halftone / effects.descriptions.halftone
    icon: '⬡',
    color: 'from-purple-500 to-pink-500',
    category: 'printing',

    // Lazy Loading - Carrega apenas quando necessário
    loader: () => import('./halftone'),

    // Metadata carregada sincronamente (leve)
    defaultSettings: {
      intensity: 1.0,
      gridSize: 15,
      smoothing: 0.15
    }
  },

  halftoneCmykCartoon: {
    id: 'halftoneCmykCartoon',
    icon: '⬡',
    color: 'from-purple-600 to-pink-500',
    category: 'printing',
    loader: () => import('./halftone-cmyk-cartoon'),
    defaultSettings: {
      intensity: 1.0,
      dotsPerScreen: 100.0,
      edgeScale: 1.0
    }
  },

  cmykSplit: {
    id: 'cmykSplit',
    icon: '🎨',
    color: 'from-cyan-500 to-magenta-500',
    category: 'printing',
    loader: () => import('./cmyk-split'),
    defaultSettings: {
      intensity: 1.0,
      radius: 0.125
    }
  },

  hatchBlur: {
    id: 'hatchBlur',
    name: 'Hatch Blur',
    description: 'Desfoque com padrão de hachura cruzada',
    icon: '≋',
    color: 'from-blue-500 to-cyan-500',
    category: 'blur',
    illustration: '/assets/effects/blur/HatchBlur.webp',
    loader: () => import('./hatch-blur'),
    defaultSettings: {
      width: 0.1,
      angle: 0.125,
      quality: 16
    }
  },

  zoomBlur: {
    id: 'zoomBlur',
    name: 'Zoom Blur',
    description: 'Desfoque radial com efeito de zoom',
    icon: '⊞',
    color: 'from-orange-500 to-red-500',
    category: 'blur',
    illustration: '/assets/effects/blur/ZoomBlur.webp',
    loader: () => import('./zoom-blur'),
    defaultSettings: {
      strength: 0.25,
      centerX: 0.5,
      centerY: 0.5
    }
  },

  traceEdges: {
    id: 'traceEdges',
    name: 'Trace Edges',
    description: 'Detecção e realce de bordas',
    icon: '⊡',
    color: 'from-green-500 to-teal-500',
    category: 'detection',
    loader: () => import('./trace-edges'),
    defaultSettings: {
      intensity: 4.2,
      threshold: 0.0,
      sobel: true,
      opaque: true,
      fade: 0.01
    }
  },

  ditherBlur: {
    id: 'ditherBlur',
    name: 'Dither Blur',
    description: 'Blur circular com padrão pseudo-aleatório',
    icon: '⊚',
    color: 'from-indigo-500 to-purple-500',
    category: 'blur',
    illustration: '/assets/effects/blur/DitherBlur.webp',
    loader: () => import('./dither-blur'),
    defaultSettings: {
      amount: 0.1,
      maxRadius: 0.1,
      jitter: false
    }
  },

  newspapersDithering: {
    id: 'newspapersDithering',
    name: 'Newspapers Dithering',
    description: 'Dithering tipo jornal (padrão senoidal + luminância)',
    icon: '📰',
    color: 'from-gray-600 to-gray-900',
    category: 'printing',
    loader: () => import('./newspapers-dithering'),
    defaultSettings: {
      intensity: 1.0
    }
  },

  gaussianBlur: {
    id: 'gaussianBlur',
    name: 'Gaussian Blur',
    description: 'Desfoque suave e natural',
    icon: '〰️',
    color: 'from-blue-400 to-indigo-500',
    category: 'blur',
    illustration: '/assets/effects/blur/GaussianBlur.webp',
    loader: () => import('./gaussian-blur'),
    defaultSettings: {
      radius: 15,
      intensity: 1.0
    }
  },

  sketch: {
    id: 'sketch',
    name: 'Rascunho',
    description: 'Efeito de desenho a lápis com cross-hatching',
    icon: '✏️',
    color: 'from-indigo-500 to-purple-500',
    category: 'artistic',
    loader: () => import('./sketch'),
    defaultSettings: {
      intensity: 1.0,
      angleNum: 3,
      lineWidth: 1.0,
      timeOffset: 0.0
    }
  },

  bwSketch: {
    id: 'bwSketch',
    name: 'B&W Sketch',
    description: 'Efeito de desenho P&B com detecção de bordas',
    icon: '🖊️',
    color: 'from-slate-500 to-gray-600',
    category: 'artistic',
    loader: () => import('./bw-sketch'),
    defaultSettings: {
      distortAmount: 1.0,
      edgeIntensity: 1.0,
      distortAll: false,
      invertColor: true,
      timeOffset: 0.0
    }
  },

  sketchBook: {
    id: 'sketchBook',
    name: 'Sketch Book',
    description: 'Efeito avançado de desenho a mão com textura de papel',
    icon: '📖',
    color: 'from-amber-500 to-orange-500',
    category: 'artistic',
    loader: () => import('./sketch-book'),
    defaultSettings: {
      intensity: 1.0,
      angleNum: 3,
      lineWidth: 1.0,
      paperTexture: 1.0,
      drawingOpacity: 1.0,
      vignetteStrength: 1.0,
      timeOffset: 0.0
    }
  },

  bwSketchNew: {
    id: 'bwSketchNew',
    name: 'Black & White Sketch',
    description: 'Efeito de desenho P&B com distorção de triângulo',
    icon: '✏️',
    color: 'from-gray-500 to-slate-600',
    category: 'artistic',
    loader: () => import('./bw-sketch-new'),
    defaultSettings: {
      distortAmount: 1.0,
      edgeIntensity: 1.0,
      distortAll: false,
      invertColor: true,
      timeOffset: 0.0
    }
  },

  drawingPractice2: {
    id: 'drawingPractice2',
    name: 'Drawing practice #2',
    description: 'Técnica avançada de crosshatching com espaço de cores Oklab',
    icon: '🎨',
    color: 'from-purple-500 to-violet-600',
    category: 'artistic',
    loader: () => import('./drawing-practice-2'),
    defaultSettings: {
      crosshatchIntensity: 1.0,
      edgeThreshold: 1.0,
      strokeWidth: 0.5,
      strokeDensity: 4.0,
      crosshatchModel: 2,
      timeOffset: 0.0
    }
  },

  crossHatchNatron: {
    id: 'crossHatchNatron',
    name: 'Cross hatch natron',
    description: 'Técnica de crosshatching com detecção de bordas Sobel',
    icon: '🎯',
    color: 'from-green-500 to-emerald-600',
    category: 'artistic',
    loader: () => import('./cross-hatch-natron'),
    defaultSettings: {
      density: 9.0,
      width: 5.0,
      hatch1: 0.8,
      hatch2: 0.6,
      hatch3: 0.3,
      hatch4: 0.15,
      hatch1Brightness: 0.8,
      hatch2Brightness: 0.6,
      hatch3Brightness: 0.3,
      hatch4Brightness: 0.0,
      edgeIntensity: 1.0
    }
  },

  oilPaintBrush: {
    id: 'oilPaintBrush',
    name: 'Oil paint brush',
    description: 'Aplica iluminação direcional sobre a imagem com efeito de relevo',
    icon: '🖌️',
    color: 'from-amber-500 to-orange-600',
    category: 'arte',
    loader: () => import('./oil-paint-brush'),
    defaultSettings: {
      paintSpec: 0.15,
      vignette: 1.5,
      lightIntensity: 0.9,
      specularPower: 12.0,
      specularIntensity: 1.0,
      shadowIntensity: 0.1
    }
  },

  painted: {
    id: 'painted',
    name: 'Painted',
    description: 'Efeito de pintura com filtro Kuwahara artístico',
    icon: '🎨',
    color: 'from-orange-500 to-red-600',
    category: 'arte',
    loader: () => import('./painted'),
    defaultSettings: {
      brushSize: 5.0,
      depthPerChannel: 64.0,
      timeOffset: 0.0
    }
  },

  adjustments: {
    id: 'adjustments',
    name: 'Ajustes',
    description: 'Ajuste brilho, contraste, saturação, nitidez, tonalidade e canais RGB',
    icon: '',
    color: 'from-blue-500 to-cyan-500',
    category: 'basic',
    loader: () => import('./adjustments'),
    defaultSettings: {
      brightness: 0.0,
      contrast: 0.0,
      saturation: 0.0,
      sharpness: 0.0,
      sharpnessRadius: 1.0,
      temperature: 0.0,
      tint: 0.0,
      highlights: 0.0,
      shadows: 0.0,
      exposure: 0.0,
      redShadows: 0.0,
      redHighlights: 0.0,
      greenShadows: 0.0,
      greenHighlights: 0.0,
      blueShadows: 0.0,
      blueHighlights: 0.0
    }
  },

  lensFlare: {
    id: 'lensFlare',
    name: 'Lens Flare',
    description: 'Efeito de reflexo de lente com anéis coloridos',
    icon: '✨',
    color: 'from-yellow-500 to-orange-600',
    category: 'lens',
    loader: () => import('./lens-flare'),
    defaultSettings: {
      intensity: 0.5,
      flareSize: 1.0,
      flareBrightness: 0.1,
      mouseX: 0.5,
      mouseY: 0.5,
      flareColor: [1.0, 0.9, 0.7]
    }
  },

  lensFlareOnly: {
    id: 'lensFlareOnly',
    name: 'Lens Flare Only',
    description: 'Lens flare limpo com múltiplos reflexos',
    icon: '💫',
    color: 'from-cyan-500 to-blue-600',
    category: 'lens',
    loader: () => import('./lens-flare-only'),
    defaultSettings: {
      intensity: 1.5,
      mouseX: 0.5,
      mouseY: 0.5,
      colorFactor: 0.5,
      colorMix: 0.1,
      time: 0
    }
  },

  anamorphicFlare: {
    id: 'anamorphicFlare',
    name: 'Anamorphic Lens Flare',
    description: 'Lens flare anamórfico com linhas horizontais',
    icon: '🌟',
    color: 'from-purple-500 to-pink-600',
    category: 'lens',
    loader: () => import('./anamorphic-flare'),
    defaultSettings: {
      intensity: 0.5,
      stretch: 400.0,
      brightness: 0.9,
      mouseX: 0.5,
      mouseY: 0.5
    }
  },

  lensFlareStrong: {
    id: 'lensFlareStrong',
    name: 'Strong Lens Flare',
    description: 'Strong lens flare effect with multiple rings and reflections',
    icon: '💥',
    color: 'from-yellow-400 to-orange-600',
    category: 'lens',
    loader: () => import('./lens-flare-strong'),
    defaultSettings: {
      intensity: 1.0,
      mouseX: 0.5,
      mouseY: 0.5
    }
  },

  lensFlareFun: {
    id: 'lensFlareFun',
    name: 'Fun Lens Flare',
    description: 'Fun lens flare effect with colorful radiation',
    icon: '🎆',
    color: 'from-pink-400 to-purple-600',
    category: 'lens',
    loader: () => import('./lens-flare-fun'),
    defaultSettings: {
      intensity: 1.0,
      mouseX: 0.5,
      mouseY: 0.5
    }
  },

  magicParticles: {
    id: 'magicParticles',
    name: 'Magic Particles',
    description: 'Partículas mágicas estáticas com efeito de estrela e controle de posição',
    icon: '✨',
    color: 'from-pink-500 to-purple-600',
    category: 'especiais',
    loader: () => import('./magic-particles'),
    defaultSettings: {
      intensity: 1.0,
      nbParticles: 50,
      motionBlur: 0.73,
      time: 0,
      positionX: 0.5,
      positionY: 0.5,
      zoom: 1.0
    }
  },

  magicParticlesV2: {
    id: 'magicParticlesV2',
    name: 'Magic Particles V2',
    description: 'Partículas mágicas avançadas com estrelas de 8 pontas (estáticas com controle de posição)',
    icon: '🌟',
    color: 'from-purple-500 to-pink-600',
    category: 'especiais',
    loader: () => import('./magic-particles-v2'),
    defaultSettings: {
      intensity: 1.0,
      nbParticles: 50,
      motionBlur: 0.73,
      time: 0,
      positionX: 0.5,
      positionY: 0.5,
      zoom: 1.0
    }
  },

  plasmaWaves: {
    id: 'plasmaWaves',
    name: 'Plasma Waves',
    description: 'Ondas de plasma estáticas com efeito de tela (screen) sci-fi',
    icon: '🌊',
    color: 'from-cyan-500 to-blue-600',
    category: 'especiais',
    loader: () => import('./plasma-waves'),
    defaultSettings: {
      intensity: 0.5,
      speed: 0.2,
      scale: 5.0,
      time: 0,
      positionY: 0.5,
      waveCount: 16,
      timeOffset: 0,
      rotation: 0
    }
  },

  circlePlasma: {
    id: 'circlePlasma',
    name: 'Circle Plasma',
    description: 'Plasma circular com ondas fluidas e cores vibrantes',
    icon: '⭕',
    color: 'from-purple-500 to-pink-600',
    category: 'especiais',
    loader: () => import('./circle-plasma'),
    defaultSettings: {
      intensity: 0.5,
      iterations: 8,
      brightness: 7.0,
      colorShift: 0,
      timeOffset: 0
    }
  },

  blueNoise: {
    id: 'blueNoise',
    name: 'Blue Noise',
    description: 'Dithering com ruído azul procedural usando algoritmo Voronoi',
    icon: '⚡',
    color: 'from-blue-500 to-indigo-600',
    category: 'outros',
    loader: () => import('./blue-noise'),
    defaultSettings: {
      intensity: 0.5,
      ditherSize: 4.0,
      threshold: 0.5,
      seed: 0
    }
  },

  multicolor: {
    id: 'multicolor',
    name: 'Multicolor',
    description: 'Aplica cores diferentes nos 4 cantos da imagem',
    icon: '🌈',
    color: 'from-pink-500 to-purple-600',
    category: 'cores',
    loader: () => import('./multicolor'),
    defaultSettings: {
      color1: '#ff0000',
      color2: '#00ff00',
      color3: '#0000ff',
      color4: '#ffffff',
      rotationAngle: 0.0,
      intensity: 1.0
    }
  },

  shadowColor: {
    id: 'shadowColor',
    name: 'Shadow Color',
    description: 'Mapeia luminosidade da imagem em 3 cores (thermal vision)',
    icon: '🌡️',
    color: 'from-blue-500 to-red-600',
    category: 'cores',
    loader: () => import('./shadow-color'),
    defaultSettings: {
      darkColor: '#002aa3',
      midColor: '#00ff00',
      brightColor: '#ff0000',
      intensity: 1.0
    }
  },

  asciiArt: {
    id: 'asciiArt',
    name: 'ASCII Art',
    description: 'Converte a imagem em arte ASCII baseada em níveis de cinza',
    icon: '💻',
    color: 'from-gray-700 to-green-600',
    category: 'printing',
    loader: () => import('./ascii-art'),
    defaultSettings: {
      amount: 0.3,
      intensity: 1.0
    }
  },

  asciiArtColor: {
    id: 'asciiArtColor',
    name: 'ASCII Art Color',
    description: 'Converte a imagem em arte ASCII colorida preservando as cores originais',
    icon: '🎨',
    color: 'from-cyan-500 to-blue-600',
    category: 'printing',
    loader: () => import('./ascii-art-color'),
    defaultSettings: {
      blockSize: 8.0,
      intensity: 1.0
    }
  },

  pixelTransform: {
    id: 'pixelTransform',
    name: 'PixelTransform',
    description: 'Decompõe a imagem em formas geométricas (quadrados, círculos, triângulos, etc)',
    icon: '🔷',
    color: 'from-indigo-500 to-purple-600',
    category: 'arte',
    loader: () => import('./pixel-transform'),
    defaultSettings: {
      cellSize: 20.0,
      shapeType: 0,
      edgeSize: 0.8,
      rotation: 0.0,
      bgColor: '#000000',
      showBackground: true,
      bgAlpha: 1.0,
      intensity: 1.0
    }
  },

  neon: {
    id: 'neon',
    name: 'Neon',
    description: 'Efeito de bordas neon com god rays radiais',
    icon: '✨',
    color: 'from-pink-500 to-purple-600',
    category: 'cores',
    loader: () => import('./neon'),
    defaultSettings: {
      intensity: 5.0,
      gain: 1.0,
      neonColor: '#ff66a3',
      effectIntensity: 1.0
    }
  },

  mysticGrid: {
    id: 'mysticGrid',
    name: 'Mystic Grid',
    description: 'Rede mística com partículas cintilantes e profundidade 3D',
    icon: '🔮',
    color: 'from-purple-500 to-indigo-600',
    category: 'animation',
    loader: () => import('./mystic-grid'),
    defaultSettings: {
      time: 5.0,
      intensity: 0.5,
      numLayers: 2.0,
      speed: 0.5,
      sparkle: 0.5,
      rotation: 0.0,
      baseColor: '#8866ff'
    }
  },


  testMultipass: {
    id: 'testMultipass',
    name: 'TEST Multi-Pass',
    description: 'Teste básico do sistema multi-pass (debug)',
    icon: '🧪',
    color: 'from-red-500 to-orange-600',
    category: 'other',
    loader: () => import('./test-multipass'),
    defaultSettings: {}
  },

  rainScreen: {
    id: 'rainScreen',
    name: 'Rain Screen',
    description: 'Gotas de chuva animadas na tela com distorção realista',
    icon: '🌧️',
    color: 'from-blue-400 to-cyan-500',
    category: 'animation',
    loader: () => import('./rain-screen'),
    defaultSettings: {
      intensity: 0.05,
      scale: 25.0,
      speed: 1.0,
      sharpness: 1.0
    }
  },

  ditherBayer: {
    id: 'ditherBayer',
    name: 'Dither Bayer',
    description: 'Dithering estilo Bayer com transição circular',
    icon: '⬛',
    color: 'from-gray-600 to-slate-700',
    category: 'printing',
    loader: () => import('./dither-bayer'),
    defaultSettings: {
      colorize: 0.0,
      matrixMode: 2
    }
  },

  dawnBringer: {
    id: 'dawnBringer',
    name: 'DawnBringer',
    description: 'Paleta de 16 cores com dithering artístico',
    icon: '🌅',
    color: 'from-purple-500 to-pink-600',
    category: 'arte',
    loader: () => import('./dawn-bringer'),
    defaultSettings: {
      pixelSize: 4.0
    }
  },

  pixelArtPalette: {
    id: 'pixelArtPalette',
    name: 'Pixel Art Palette',
    description: 'Paleta Oil-6 com pixelização e dithering ordenado',
    icon: '🎮',
    color: 'from-indigo-500 to-purple-600',
    category: 'arte',
    loader: () => import('./pixel-art-palette'),
    defaultSettings: {
      pixelScale: 2.0,
      ditherSpread: 0.166
    }
  },

  dotDamme: {
    id: 'dotDamme',
    name: 'Dot Damme',
    description: 'Halftone em pontos baseado no conteúdo da imagem',
    icon: '◌',
    color: 'from-gray-500 to-slate-700',
    category: 'printing',
    loader: () => import('./dot-damme'),
    defaultSettings: {
      gridSize: 120.0
    }
  },

  desenho99: {
    id: 'desenho99',
    name: 'Desenho99',
    description: 'Detecção de bordas artística com controles avançados',
    icon: '✏️',
    color: 'from-indigo-500 to-purple-600',
    category: 'artistic',
    loader: () => import('./desenho99'),
    defaultSettings: {
      edgeThreshold: 0.5,
      gammaAmount: 0.5,
      bgBlackWhite: false,
      bgColor: false,
      fgColor: false,
      flip: false,
      gammaContrast: false
    }
  },

  distorcaoPolar: {
    id: 'distorcaoPolar',
    name: 'Distorção Polar',
    description: 'Efeito de distorção circular com múltiplas camadas sobrepostas',
    icon: '🌀',
    color: 'from-pink-500 to-purple-600',
    category: 'outros',
    loader: () => import('./distorcao-polar'),
    defaultSettings: {
      timeOffset: 0.0,
      amplitude: 0.5,
      frequency: 3.0,
      layers: 3.0
    }
  },

  distorcaoCaotica: {
    id: 'distorcaoCaotica',
    name: 'Distorção Caótica',
    description: 'Efeito psicodélico com múltiplas camadas distorcidas',
    icon: '🎭',
    color: 'from-purple-500 to-pink-600',
    category: 'outros',
    loader: () => import('./distorcao-caotica'),
    defaultSettings: {
      timeOffset: 0.0,
      intensity: 1.0
    }
  },

  selectColorRange: {
    id: 'selectColorRange',
    name: 'Select Color Range',
    description: 'Seleciona e isola uma faixa de cores da imagem, similar ao Color Range do Photoshop',
    icon: '🎨',
    color: 'from-pink-500 to-purple-500',
    category: 'outros',
    loader: () => import('./select-color-range'),
    defaultSettings: {
      targetColor: [0.5, 0.5, 0.5],
      range: 0.1,
      fuzziness: 0.3,
      intensity: 1.0
    }
  },

  dotRipples: {
    id: 'dotRipples',
    name: 'Dot Ripples',
    description: 'Ondulações em pontos baseadas no conteúdo da imagem',
    icon: '◌',
    color: 'from-gray-500 to-black',
    category: 'outros',
    loader: () => import('./dot-ripples'),
    defaultSettings: { }
  },

  cartoonComicbook: {
    id: 'cartoonComicbook',
    name: 'Cartoon/ComicBook FX',
    description: 'Transforma a imagem em estilo quadrinhos com halftone, bilateral filter e detecção de bordas',
    icon: '📚',
    color: 'from-orange-500 to-red-500',
    category: 'outros',
    loader: () => import('./cartoon-comicbook'),
    defaultSettings: {
      edgeStrength: 2.0,
      dotSize: 4.0,
      smoothness: 2.0,
      intensity: 1.0
    }
  },

  simpleCartoon: {
    id: 'simpleCartoon',
    name: 'Simple Cartoon',
    description: 'Efeito simples de cartoon usando quantização de luminância YUV',
    icon: '🎭',
    color: 'from-yellow-500 to-orange-500',
    category: 'outros',
    loader: () => import('./simple-cartoon'),
    defaultSettings: {
      levels: 2.0,
      intensity: 1.0
    }
  },

  chromaticAberration: {
    id: 'chromaticAberration',
    name: 'Chromatic Aberration',
    description: 'Aberração cromática com distorção barrel e separação RGB',
    icon: '🌈',
    color: 'from-red-500 to-blue-500',
    category: 'outros',
    loader: () => import('./chromatic-aberration'),
    defaultSettings: {
      distortion: 0.2,
      strength: 0.01,
      separation: 0.1,
      iterations: 10,
      intensity: 1.0
    }
  },

  crtTv: {
    id: 'crtTv',
    name: 'CRT TV',
    description: 'Efeito de TV CRT vintage com scanlines, aberração cromática e vignette',
    icon: '📺',
    color: 'from-green-500 to-teal-500',
    category: 'outros',
    loader: () => import('./crt-tv'),
    defaultSettings: {
      scanlineIntensity: 0.5,
      chromaticAberration: 15.0,
      vignetteStrength: 0.5,
      distortionAmount: 0.3,
      intensity: 1.0
    }
  },

  sketchyStippling: {
    id: 'sketchyStippling',
    name: 'Sketchy Stippling',
    description: 'Efeito de desenho a lápis com pontilhismo',
    icon: '✏️',
    color: 'from-gray-600 to-slate-700',
    category: 'outros',
    loader: () => import('./sketchy-stippling'),
    defaultSettings: {
      dotDensity: 20.0,
      dotSize: 2.0,
      threshold: 0.5,
      intensity: 1.0
    }
  },

  lensFlareUnified: {
    id: 'lensFlareUnified',
    name: 'Lens Flare Effects',
    description: 'Múltiplos estilos de lens flare com anéis, halos e starburst',
    icon: '✨',
    color: 'from-yellow-400 to-orange-500',
    category: 'outros',
    loader: () => import('./lens-flare'),
    defaultSettings: {
      style: 0,
      centerX: 0.5,
      centerY: 0.5,
      brightness: 1.0,
      scale: 1.0,
      intensity: 1.0
    }
  },

  tiledMosaic: {
    id: 'tiledMosaic',
    name: 'Tiled Mosaic',
    description: 'Efeito de mosaico estático com grade configurável',
    icon: '🧩',
    color: 'from-green-400 to-blue-500',
    category: 'mosaicos',
    loader: () => import('./tiled-mosaic'),
    defaultSettings: {
      gridWidth: 5.0,
      gridHeight: 10.0,
      borderWidth: 1.0,
      borderColor: [0.0, 0.0, 0.0],
      intensity: 1.0
    }
  },

  mosaic5: {
    id: 'mosaic5',
    name: 'Mosaic 5',
    description: 'Mosaico hexagonal complexo com 6 triângulos por hexágono',
    icon: '🔷',
    color: 'from-purple-400 to-pink-500',
    category: 'mosaicos',
    loader: () => import('./mosaic-5'),
    defaultSettings: {
      hexagonRadius: 16.0,
      intensity: 1.0
    }
  },

  leadlightMosaic: {
    id: 'leadlightMosaic',
    name: 'Leadlight Mosaic',
    description: 'Mosaico baseado em padrões de Voronoi que simula o estilo de vitral (leadlight)',
    icon: '🪟',
    color: 'from-amber-400 to-orange-500',
    category: 'mosaicos',
    loader: () => import('./leadlight-mosaic'),
    defaultSettings: {
      scale: 31.0,
      contrast: 1.0,
      borderWidth: 0.002,
      intensity: 1.0,
      highlight: false,
      backgroundColor: [0.0, 0.0, 0.0]
    }
  },

  pixelleteStars: {
    id: 'pixelleteStars',
    name: 'Pixellete Stars',
    description: 'Efeito de estrelas pixeladas com mosaico',
    icon: '⭐',
    color: 'from-yellow-400 to-orange-500',
    category: 'outros',
    loader: () => import('./pixellete-stars'),
    defaultSettings: {
      mosaicRadius: 10.0,
      starRays: 7.0,
      starIntensity: 1.0,
      intensity: 1.0
    }
  },

  legofied: {
    id: 'legofied',
    name: 'Legofied',
    description: 'Transforma a imagem em blocos LEGO com studs',
    icon: '🧱',
    color: 'from-red-500 to-yellow-500',
    category: 'outros',
    loader: () => import('./legofied'),
    defaultSettings: {
      brickSize: 20.0,
      studHeight: 0.3,
      colorReduction: 8.0,
      intensity: 1.0
    }
  },

  pointilism: {
    id: 'pointilism',
    name: 'Pointilism',
    description: 'Efeito de pintura pontilhista com pontos coloridos',
    icon: '🎨',
    color: 'from-purple-500 to-pink-500',
    category: 'outros',
    loader: () => import('./pointilism'),
    defaultSettings: {
      pointDensity: 0.2,
      pointSize: 0.6,
      time: 5.0
    }
  },

  tinyToon: {
    id: 'tinyToon',
    name: 'Tiny Toon',
    description: 'Efeito cartoon com detecção de bordas e posterização',
    icon: '🎬',
    color: 'from-blue-500 to-purple-500',
    category: 'outros',
    loader: () => import('./tiny-toon'),
    defaultSettings: {
      posterizeLevels: 5.0,
      edgePower: 0.5
    }
  },

  blockGlitch: {
    id: 'blockGlitch',
    name: 'Block Glitch',
    description: 'Efeito de glitch digital com blocos e separação RGB',
    icon: '⚡',
    color: 'from-cyan-500 to-purple-500',
    category: 'outros',
    loader: () => import('./block-glitch'),
    defaultSettings: {
      blockSize: 20.0,
      glitchAmount: 0.5,
      rgbSeparation: 5.0,
      intensity: 1.0
    }
  },

  legoArt: {
    id: 'legoArt',
    name: 'Lego Art',
    description: 'Mosaico de arte LEGO com padrões circulares ou quadrados',
    icon: '🎨',
    color: 'from-orange-500 to-red-500',
    category: 'outros',
    loader: () => import('./lego-art'),
    defaultSettings: {
      mosaicSize: 15.0,
      shapeType: 0,
      colorPalette: 16.0,
      intensity: 1.0
    }
  },

  watercolor: {
    id: 'watercolor',
    name: 'Watercolor',
    description: 'Efeito de aquarela com textura de papel e difusão de cores',
    icon: '🎨',
    color: 'from-blue-300 to-purple-400',
    category: 'arte',
    loader: () => import('./watercolor/index.js'),
    defaultSettings: {
      intensity: 0.8,
      paperTexture: 0.5,
      edgeDarkness: 0.3
    }
  }
};

/**
 * Pega metadata do efeito (síncrono - sem carregar código)
 */
export const getEffectMetadata = (id) => {
  const config = EFFECTS_REGISTRY[id];
  if (!config) {
    console.error(`Effect ${id} not found in registry`);
    return null;
  }

  const { loader, ...metadata } = config;
  return metadata;
};

/**
 * Carrega efeito completo (assíncrono - carrega código sob demanda)
 */
export const loadEffect = async (id) => {
  const config = EFFECTS_REGISTRY[id];
  if (!config) {
    throw new Error(`Effect ${id} not found in registry`);
  }

  console.log(`🔄 Loading effect: ${config.name}...`);
  const startTime = performance.now();

  const module = await config.loader();

  const loadTime = performance.now() - startTime;
  console.log(`✅ Effect ${config.name} loaded in ${loadTime.toFixed(2)}ms`);

  return {
    ...config,
    hook: module.useEffect,
    Controls: module.Controls,
    shaders: module.shaders
  };
};

/**
 * Lista todos os efeitos (síncrono - só metadata)
 */
export const getAllEffectsMetadata = () => {
  return Object.keys(EFFECTS_REGISTRY).map(id => getEffectMetadata(id));
};

/**
 * Agrupa efeitos por categoria
 */
export const getEffectsByCategory = () => {
  const byCategory = {};

  Object.keys(EFFECTS_REGISTRY).forEach(id => {
    const metadata = getEffectMetadata(id);
    const category = metadata.category || 'outros';

    if (!byCategory[category]) {
      byCategory[category] = [];
    }

    byCategory[category].push(metadata);
  });

  return byCategory;
};
